import React, { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { StarsIcon } from "lucide-react";
import { ReactTyped } from "react-typed";
import { trackButtonBookDemoClick, trackButtonClick } from '../../utils/gtm';

const QUALIFICATION_ROLES = [
  { label: "CTO", value: "CTO", icon: "🏗️", desc: "Chief Technology Officer" },
  { label: "CEO", value: "CEO", icon: "🎯", desc: "Chief Executive Officer" },
  { label: "Developer", value: "Developer", icon: "💻", desc: "Software Engineer / Developer" },
  { label: "QA Lead", value: "QA Lead", icon: "🔍", desc: "QA Lead / Test Engineer" },
  { label: "Engineering Manager", value: "Engineering Manager", icon: "📊", desc: "Engineering Manager / VP Eng" },
  { label: "Other", value: "Other", icon: "👤", desc: "Other role" },
];

// Feature cards data
const featureCards = [
  {
    png: "/assets/landing/heroSec1.png",
    webp: "/assets/landing/heroSec1.webp",
    avif: "/assets/landing/heroSec1.avif",
    title: "Automated code review suggestions shown in a pull request."
  },
  {
    png: "/assets/landing/heroSec2.png",
    webp: "/assets/landing/heroSec2.webp",
    avif: "/assets/landing/heroSec2.avif",
    title: "AI-powered code analysis identifying potential bugs and vulnerabilities."
  },
  {
    png: "/assets/landing/heroSec3.png",
    webp: "/assets/landing/heroSec3.webp",
    avif: "/assets/landing/heroSec3.avif",
    title: "Dashboard showing code quality metrics and trends over time."
  },
  {
    png: "/assets/landing/heroSec4.png",
    webp: "/assets/landing/heroSec4.webp",
    avif: "/assets/landing/heroSec4.avif",
    title: "Integration with popular version control systems like GitHub and Bitbucket."
  }
];

const HeroSection = () => {
  const navigate = useNavigate();
  const [showQualification, setShowQualification] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');

  const handleGetStarted = useCallback(() => {
    trackButtonClick('start_free_trial', 'hero_section_landing_page');
    navigate('/signup');
  }, [navigate]);
  
  const handleWatchDemo = useCallback(() => {
    trackButtonClick('book_demo', 'hero_section_landing_page');
    setShowQualification(true);
  }, []);

  const handleRoleSelect = useCallback((role) => {
    setSelectedRole(role);
    trackButtonClick('qualification_role_selected', 'hero_section_landing_page', { role });
    if (window.fbq) window.fbq('track', 'Lead', { content_name: 'book_sniffr_demo', content_category: role });
    setShowQualification(false);
    setShowCalendly(true);
  }, []);

  const handleCloseQualification = useCallback(() => {
    setShowQualification(false);
  }, []);

  const handleCloseCalendly = useCallback(() => {
    setShowCalendly(false);
    setSelectedRole('');
  }, []);

  const calendlyUrl = selectedRole
    ? `https://calendly.com/sniffr-sales/30min?a1=${encodeURIComponent(selectedRole)}`
    : 'https://calendly.com/sniffr-sales/30min';

  // Load Calendly script
  useEffect(() => {
    if (showCalendly) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, [showCalendly]);

  return (
    <Box
      sx={{
        position: "relative",
        pt: 2,
        backgroundColor: "#ffffff",
        mt: 6
      }}
    >
      {/* Background grid */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: "url('/assets/landing/lines.png')",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center top",
          backgroundSize: { xs: "1200px auto", md: "cover" },
          opacity: 0.6,
          pointerEvents: "none"
        }}
      />
      {/* Decorative gear icons - Hidden on mobile */}
      <Box aria-hidden sx={{ position: "absolute", top: 160, right: 80, display: { xs: "none", md: "block" } }}>
        <Box sx={{ position: "absolute", width: 30, height: 30, top: -32, right: -30, borderRadius: "50%", display: "grid", placeItems: "center", border: "1px solid #E5E7EB", background: "rgba(255,255,255,0.9)", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
          <picture style={{ width: "100%", height: "100%", display: "block" }}>
            <source srcSet="/assets/landing/Bug2.avif" type="image/avif" />
            <source srcSet="/assets/landing/Bug2.webp" type="image/webp" />
            <Box component="img" src="/assets/landing/Bug2.png" alt="" aria-hidden loading="lazy"
              sx={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit", display: "block" }}
              onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
            />
          </picture>
        </Box>
        <Box sx={{ width: 100, height: 10, display: "grid", placeItems: "center", position: "absolute", left: -30, top: -190, }}>
          <picture style={{ width: "100%", height: "100%", display: "block" }}>
            <source srcSet="/assets/landing/Vector2.avif" type="image/avif" />
            <source srcSet="/assets/landing/Vector2.webp" type="image/webp" />
            <Box component="img" src="/assets/landing/Vector2.png" alt="" aria-hidden loading="lazy"
              sx={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "inherit", display: "block" }}
              onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
            />
          </picture>
        </Box>
      </Box>
      <Box aria-hidden sx={{ position: "absolute", left: 100, top: 280, display: { xs: "none", md: "block" } }}>
        <Box sx={{ width: 30, height: 30, display: "grid", placeItems: "center", }}>
          <picture style={{ width: "100%", height: "100%", display: "block" }}>
            <source srcSet="/assets/landing/Bug1.avif" type="image/avif" />
            <source srcSet="/assets/landing/Bug1.webp" type="image/webp" />
            <Box component="img" src="/assets/landing/Bug1.png" alt="" aria-hidden loading="lazy"
              sx={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit", display: "block" }}
              onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
            />
          </picture>
        </Box>
        <Box sx={{ width: 120, height: 500, display: "grid", placeItems: "center", position: "absolute", left: -105, top: -240, }}>
          <picture style={{ width: "100%", height: "100%", display: "block" }}>
            <source srcSet="/assets/landing/Vector1.avif" type="image/avif" />
            <source srcSet="/assets/landing/Vector1.webp" type="image/webp" />
            <Box component="img" src="/assets/landing/Vector1.png" alt="" aria-hidden loading="lazy"
              sx={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "inherit", display: "block" }}
              onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
            />
          </picture>
        </Box>
      </Box>
      <Container
        maxWidth="xl"
        sx={{
          px: { xs: 3, sm: 4, md: 6, lg: 20, xl: 20 },
          maxWidth: { xs: "100%", sm: "100%", md: "100%", lg: "1440px", xl: "1600px" },
          mx: "auto",
          textAlign: "start",
          fontFamily: "MD IO, monospace"
        }}
      >
        {/* Notification Banner */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Chip
            icon={<StarsIcon size={16} color="#f97316" />}
            label="New: AI-Powered Code & Requirement Reviews"
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.95)",
              color: "#4D4D4D",
              fontWeight: 600,
              px: { xs: 2, sm: 3 },
              py: { xs: 0.5, sm: 1 },
              fontSize: '14px',
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              border: "1px solid #E6E6E6",
              fontFamily: "Segoe UI, monospace"
            }}
          />
        </Box>

        {/* Main Hero Content - Centered */}
        <Box sx={{
          textAlign: "center",
          mx: "auto",
        }}>
          {/* Main Headlines */}
          <Typography
            variant="h1"
            sx={{
              fontFamily: "MD IO, monospace",
              fontSize: { xs: '28px', sm: '32px', md: '38px' },
              fontWeight: 600,
              mb: { xs: 3, sm: 3, md: 4 },
              color: "#141414",
              lineHeight: { xs: '38px', sm: '42px', md: '50px' },
              letterSpacing: "0%",
              px: { xs: 1, sm: 0 }
            }}
          >
            Ship Code with{" "}
            <Typography
              component="span"
              sx={{
                background: "linear-gradient(90deg, #A27ED0 33.17%, #00BFFF 50%, #FF8052 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontWeight: 600,
                fontSize: "inherit",
                fontFamily: "MD IO, monospace"
              }}
            >
              Confidence, Speed &{" "}
            </Typography>
            <Typography
              component="span"
              sx={{
                background: "linear-gradient(90deg, #A27ED0 0%, #FF8052 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontWeight: 600,
                fontSize: "inherit",
                fontFamily: "MD IO, monospace"
              }}
            >
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  minWidth: "200px",
                  textAlign: "left",
                  position: "relative",
                  '& .typed-cursor': {
                    animation: 'blink 1s infinite',
                    color: '#FF8052 !important',
                    background: 'none !important',
                    WebkitTextFillColor: '#FF8052 !important'
                  },
                  '@keyframes blink': {
                    '0%, 49%': { opacity: 1 },
                    '50%, 100%': { opacity: 0 }
                  },
                  '@keyframes errorHighlight': {
                    '0%, 100%': {
                      textDecoration: 'underline wavy #ef4444',
                      textUnderlineOffset: '10px',
                      backgroundColor: 'rgba(239, 68, 68, 0.1)'
                    }
                  },
                  '& .typed-text-error': {
                    animation: 'errorHighlight 0.5s ease-in-out',
                    textDecoration: 'underline wavy #ef4444',
                    textUnderlineOffset: '10px',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    borderRadius: '2px',
                    padding: '0 4px'
                  }
                }}
              >
                <ReactTyped
                  strings={[
                    '<span style="background: linear-gradient(90deg, #A27ED0 0%, #FF8052 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">Cla</span><span class="typed-text-error" style="background: linear-gradient(90deg, #A27ED0 0%, #FF8052 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">irty</span>^1000',
                    '<span style="background: linear-gradient(90deg, #A27ED0 0%, #FF8052 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">Clarity</span>^1500'
                  ]}
                  typeSpeed={200}
                  backSpeed={100}
                  loop
                  backDelay={1000}
                  startDelay={300}
                  cursorChar="_"
                  showCursor={true}
                  smartBackspace={true}
                  contentType="html"
                />
              </Box>
              <br /> Powered by Sniffr.ai
            </Typography>
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: "16px", sm: "17px", md: "18px" },
              color: "#6b7280",
              mb: { xs: 4, sm: 6, md: 8 },
              lineHeight: 1.5,
              maxWidth: { xs: "100%", sm: "700px", md: "800px" },
              mx: "auto",
              px: { xs: 2, sm: 2 },
              fontFamily: "Segoe UI, monospace"
            }}
          >
            Spend less time chasing bugs — and more time shipping features.
          </Typography>


          {/* CTA Buttons - Centered */}
          <Box sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 3, md: 4 },
            justifyContent: "center",
            alignItems: "center",
            mb: { xs: 6, sm: 8, md: 10 },
            px: { xs: 2, sm: 0 }
          }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleGetStarted}
              sx={{
                background: "linear-gradient(88.99deg, #9756D8 -33.94%, #1A9ECA 99.13%)",
                color: "white",
                textTransform: "none",
                fontSize: "1rem",
                px: { xs: 4, sm: 5, md: 6 },
                py: { xs: 1.5, sm: 1.75, md: 2 },
                borderRadius: 1,
                fontWeight: 600,
                boxShadow: "0 4px 14px rgba(59, 130, 246, 0.3)",
                width: { xs: "100%", sm: "auto" },
                maxWidth: { xs: "300px", sm: "none" },
                fontFamily: "Segoe UI, monospace",
                "&:hover": {
                  background: "linear-gradient(135deg, #0ea5e9 0%, #3b82f6 50%, #1d4ed8 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(59, 130, 246, 0.4)"
                },
                transition: "all 0.3s ease"
              }}
            >
              Start Free Trial
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleWatchDemo}
              sx={{
                color: "#374151",
                borderColor: "#d1d5db",
                textTransform: "none",
                fontSize: "1rem",
                px: { xs: 4, sm: 5, md: 6 },
                py: { xs: 1.5, sm: 1.75, md: 2 },
                borderRadius: 1,
                fontWeight: 600,
                bgcolor: "white",
                width: { xs: "100%", sm: "auto" },
                maxWidth: { xs: "300px", sm: "none" },
                fontFamily: "Segoe UI, monospace",
                "&:hover": {
                  borderColor: "#9ca3af",
                  transform: "translateY(-2px)",
                  bgcolor: "#f9fafb"
                },
                transition: "all 0.3s ease"
              }}
            >
              Book Sniffr Demo
            </Button>
          </Box>

          {/* Feature Cards Grid */}
          <Box sx={{
            mb: { xs: 6, sm: 8, md: 10 },
            mx: "auto",
            fontFamily: "Segoe UI, monospace",
            px: { xs: 1, sm: 0 }
          }}>
            <Grid container spacing={{ xs: 2, sm: 2, md: 3, lg: 4 }} justifyContent="center">
              {featureCards.map((feature, index) => (
                <Grid item xs={6} sm={6} md={3} key={index}>
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: 2,
                      overflow: "visible",
                      background: "#F9FAFB",
                      display: "flex",
                      alignItems: "stretch",
                      justifyContent: "center",
                      transition: "transform 0.3s cubic-bezier(.4,2,.6,1), box-shadow 0.3s cubic-bezier(.4,2,.6,1)",
                      "&::after": {
                        content: "''",
                        position: "absolute",
                        left: "-10%",
                        right: "-10%",
                        bottom: "-20px",
                        height: "48px",
                        background: "linear-gradient(to bottom, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%, rgba(255,255,255,0) 100%)",
                        filter: "blur(10px)",
                        pointerEvents: "none",
                        zIndex: 0
                      },
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 10px 24px rgba(31,41,55,0.08)"
                      }
                    }}
                  >
                    <picture style={{ width: "100%", height: "100%", display: "block" }}>
                      <source srcSet={feature.avif} type="image/avif" />
                      <source srcSet={feature.webp} type="image/webp" />
                      <Box
                        component="img"
                        src={feature.png}
                        alt={feature.title}
                        loading="lazy"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          borderRadius: "inherit",
                          display: "block"
                        }}
                        onError={(e) => {
                          e.currentTarget.style.visibility = 'hidden';
                        }}
                      />
                    </picture>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

        </Box>
      </Container>

      {/* Qualification Dialog */}
      <Dialog
        open={showQualification}
        onClose={handleCloseQualification}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#141414', fontFamily: 'Segoe UI, monospace' }}>
                One quick question 👋
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5, fontFamily: 'Segoe UI, monospace' }}>
                This helps us tailor the demo to your role
              </Typography>
            </Box>
            <IconButton onClick={handleCloseQualification} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ fontWeight: 600, mb: 2, color: '#374151', fontFamily: 'Segoe UI, monospace' }}>
            What best describes your role?
          </Typography>
          <Grid container spacing={1.5}>
            {QUALIFICATION_ROLES.map((role) => (
              <Grid item xs={12} sm={6} key={role.value}>
                <Button
                  onClick={() => handleRoleSelect(role.value)}
                  aria-label={`Select role: ${role.label}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    width: '100%',
                    textTransform: 'none',
                    gap: 1.5,
                    p: 1.75,
                    border: '1.5px solid #e5e7eb',
                    borderRadius: 2,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: '#5998EF',
                      bgcolor: '#f0f7ff',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(89, 152, 239, 0.15)',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '1.5rem', lineHeight: 1 }}>{role.icon}</Typography>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#141414', fontFamily: 'Segoe UI, monospace' }}>
                      {role.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'Segoe UI, monospace' }}>
                      {role.desc}
                    </Typography>
                  </Box>
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Calendly Modal */}
      <Dialog
        open={showCalendly}
        onClose={handleCloseCalendly}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <IconButton
          onClick={() => {
            trackButtonClick('close_demo_modal', 'hero_section_landing_page');
            handleCloseCalendly();
          }}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 1)',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
          <div
            className="calendly-inline-widget"
            data-url={calendlyUrl}
            style={{ minWidth: '320px', height: '700px' }}
          ></div>
      </Dialog>
    </Box>
  );
};

export default React.memo(HeroSection);